import { useState } from 'react';
import { useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useActivateExam } from '../hooks/useActivateExam';
import { paths } from '../routes/paths';
import { examsSelectors } from '../slices/exams/examsSlice';
import type { RootState } from '../store/store';
import { MCGRAW_HILL_HANDBOOK_EXAM_ID } from './mcgrawHill';
import { selectIsHandbookActive } from './nursingCourses';

/**
 * mcgraw-hill (ORA-1261, ORA-1263): opens the Nursing vertical's course. Makes
 * the course (the handbook exam) the active exam under Preclinical, as the
 * Nursing tile's fallback does, then goes to the Dashboard. Shared by the
 * Courses page and the exam menu's Courses group. On a failure it stays put
 * with a snackbar (the exam write reports its own).
 */
export const useOpenDutCourse = () => {
  const navigate = useNavigate();
  const store = useStore<RootState>();
  const { enqueueSnackbar } = useSnackbar();
  const { activateExam } = useActivateExam();
  const [isOpening, setIsOpening] = useState(false);

  const reportFailure = () =>
    enqueueSnackbar('Unable to open this course. Please try again.', {
      variant: 'error',
      autoHideDuration: 4000,
    });

  const openCourse = async () => {
    if (isOpening) return;
    setIsOpening(true);
    let isOpened = false;
    try {
      const handbook = examsSelectors.selectById(
        store.getState(),
        MCGRAW_HILL_HANDBOOK_EXAM_ID
      );

      if (handbook && !selectIsHandbookActive(store.getState())) {
        await activateExam(handbook);
      }
      isOpened = selectIsHandbookActive(store.getState());
      // The exam write reports its own failure; a missing exam does not.
      if (!handbook) reportFailure();
    } catch {
      reportFailure();
    }

    if (isOpened) {
      navigate(paths.dashboard);
      return;
    }
    setIsOpening(false);
  };

  return { openCourse, isOpening };
};
